import type {
  NewNotifications,
  NotificationItem,
  NotificationPreferenceConfig,
} from '../types/notification';
import { decideAttention, isDocumentVisible, isQuietOrSnoozed } from './notificationAttentionPolicy';
import type { TabCoordinator } from './notificationTabCoordinator';

/**
 * Foreground attention for freshly created notifications.
 *
 * The stream only carries revisions, so on a created-revision advance we drain
 * `GET /api/notifications/new` up to a FIXED watermark and decide per item
 * whether to toast/sound. Everything that can suppress an alert is re-checked
 * here, per item, against live settings: the item's own server attention flags,
 * the current preference, quiet/snooze and tab visibility. The tab coordinator
 * dedupes by a non-content key (`created:<type>:<classroomCode>`) so a burst of
 * the same type+classroom alerts once and the same event never double-alerts
 * across tabs. Only the key and a timestamp are persisted — never content.
 */

export interface AttentionAlerterDeps {
  fetchNew(params: {
    afterCreatedRevision: string;
    throughCreatedRevision: string;
    cursor?: string | null;
  }): Promise<NewNotifications>;
  getConfig(): NotificationPreferenceConfig | null;
  getOwnCreatedRevision(): string | null;
  setOwnCreatedRevision(revision: string): void;
  coordinator: TabCoordinator;
  isVisible(): boolean;
  /** Live "user is really studying on this route" signal. */
  isStudying?(): boolean;
  onToast(item: NotificationItem): void;
  /**
   * Claim the global sound allowance. Called BEFORE the persisted sound key is
   * committed, so a rate-rejected sound never burns the 60 s dedupe key. Returns
   * false when the rate limit denied it (or when no budget is wired).
   */
  soundAccepted?(): boolean;
  onSound(volume: number | undefined): void;
  now?(): number;
}

export interface DrainResult {
  throughCreatedRevision: string | null;
  drained: number;
  alerted: number;
}

export interface AttentionAlerter {
  /**
   * Drain `(fromCreatedRevision, throughCreatedRevision]`. When `from` is
   * omitted the tab's own last-alerted watermark is used.
   */
  drainTo(
    throughCreatedRevision: string,
    fromCreatedRevision?: string,
  ): Promise<DrainResult | null>;
  /** Bounded catch-up from this tab's watermark up to the summary revision. */
  catchUp(summaryRevision: string | null | undefined): Promise<DrainResult | null>;
}

const DEFAULT_DRAIN_LIMIT = 200;
/** Catch-up pass cap: one request per reconnect, not one per missed revision. */
const RECONNECT_DRAIN_PASSES = 1;
/** Same (type+classroom) burst within this window alerts only once. */
const BURST_WINDOW_MS = 5000;
/** Keep a burst entry alive past the drain so a reconnect replay is deduped. */
const BURST_RETENTION_MS = 20_000;
const MAX_BURST_ENTRIES = 40;

export function burstKey(item: NotificationItem): string {
  return `created:${item.type}:${item.classroomCode ?? ''}`;
}

/**
 * The sound allowance is tracked per burst key but SEPARATELY from the toast
 * collapse: a row that only asked for a toast must not consume the sound that a
 * later same-key row was granted (`inbox không mất event`). The name is derived
 * from `burstKey` so a replay of the same burst still dedupes.
 */
export function burstSoundKey(item: NotificationItem): string {
  return `sound:${burstKey(item)}`;
}

/**
 * The per-NOTIFICATION persisted claim key. Distinct items therefore alert once
 * each (a burst of 12 stays 12 rows for the aggregate), while a replay of the
 * same item inside the coordinator's dedupe TTL is suppressed in every tab —
 * which a keyless leader election could not guarantee past its 5 s lease.
 */
export function notificationClaimKey(item: NotificationItem): string {
  return `notification:${item.id}`;
}

export function isRevisionAdvance(prev: string, next: string | null | undefined): boolean {
  if (!next) return false;
  return compareRevisions(next, prev) > 0;
}

/** Numeric-aware revision compare (handles `42` and opaque `server:42` ids). */
export function compareRevisions(a: string, b: string): number {
  if (a === b) return 0;
  const na = lastNumericSegment(a);
  const nb = lastNumericSegment(b);
  if (na != null && nb != null && na !== nb) return na < nb ? -1 : 1;
  return a < b ? -1 : 1;
}

function lastNumericSegment(value: string): number | null {
  const matches = String(value).match(/\d+/g);
  if (!matches || matches.length === 0) return null;
  const parsed = Number(matches[matches.length - 1]);
  return Number.isFinite(parsed) ? parsed : null;
}

function ageSeconds(item: NotificationItem, now: number): number {
  const stamp = item.occurredAt ?? item.createdAt;
  const parsed = Date.parse(stamp);
  if (Number.isNaN(parsed)) return Number.MAX_SAFE_INTEGER;
  return Math.max(0, (now - parsed) / 1000);
}

export function createAttentionAlerter(deps: AttentionAlerterDeps): AttentionAlerter {
  const now = deps.now ?? (() => Date.now());
  const bursts = new Map<string, number>();

  const isBurstSuppressed = (key: string, at: number): boolean => {
    const seenAt = bursts.get(key);
    return seenAt != null && at - seenAt < BURST_WINDOW_MS;
  };

  const rememberBurst = (key: string, at: number): void => {
    bursts.set(key, at);
    const cutoff = at - BURST_RETENTION_MS;
    bursts.forEach((seenAt, k) => {
      if (seenAt < cutoff) bursts.delete(k);
    });
    while (bursts.size > MAX_BURST_ENTRIES) {
      const oldest = bursts.keys().next().value as string | undefined;
      if (oldest == null) break;
      bursts.delete(oldest);
    }
  };

  const drainTo = async (
    throughCreatedRevision: string,
    fromCreatedRevision?: string,
  ): Promise<DrainResult | null> => {
    // An explicit `from` lets the caller pin the range it actually observed.
    // Falling back to the tab's own watermark keeps `catchUp` self-contained.
    const after = fromCreatedRevision ?? deps.getOwnCreatedRevision() ?? '0';
    let cursor: string | null = null;
    let drained = 0;
    let alerted = 0;

    for (let page = 0; page < DEFAULT_DRAIN_LIMIT; page += 1) {
      const result = await deps.fetchNew({
        afterCreatedRevision: after,
        throughCreatedRevision,
        cursor,
      });
      for (const item of result.items) {
        drained += 1;
        if (alertIfFresh(item)) alerted += 1;
      }
      cursor = result.nextCursor;
      if (cursor == null || result.items.length === 0) break;
    }

    // Advancing only after the drain resolves keeps a failed fetch from
    // skipping the items it was supposed to alert for.
    deps.setOwnCreatedRevision(throughCreatedRevision);
    return { throughCreatedRevision, drained, alerted };
  };

  function alertIfFresh(item: NotificationItem): boolean {
    const cfg = deps.getConfig();
    if (!cfg) return false;
    const at = now();
    const visible = deps.isVisible() && isDocumentVisible();
    const { quiet, snooze } = isQuietOrSnoozed(cfg, new Date(at));
    const decision = decideAttention({
      itemToast: item.attention?.toast === true,
      itemSound: item.attention?.sound === true,
      toastEnabled: cfg.toastEnabled,
      soundEnabled: cfg.soundEnabled,
      mutedWhileStudying: cfg.muteWhileStudying,
      studying: deps.isStudying?.() === true,
      quietActive: quiet,
      snoozeActive: snooze,
      visible,
      ageSeconds: ageSeconds(item, at),
      // Toast collapsing and the sound allowance are both applied downstream (the
      // provider aggregates the burst; sound is claimed per key below), so the
      // alerter must not pre-suppress a row that still owes attention.
      soundSuppressed: isBurstSuppressed(burstSoundKey(item), at),
    });
    if (!decision.toast && !decision.sound) return false;

    const soundKey = burstSoundKey(item);
    // Every distinct notification claims its OWN persisted key, so a replay of
    // the same item inside the 60 s dedupe TTL never toasts twice (in this tab or
    // another), while a burst of DISTINCT items still reaches the provider and is
    // folded into one aggregate carrying the real count.
    const itemKey = notificationClaimKey(item);
    let alerted = false;
    let soundPlayed = false;
    deps.coordinator.runIfLeader(() => {
      alerted = true;
      if (decision.sound) {
        // Ask the global budget FIRST: a rate-rejected sound must not burn the
        // persisted 60 s key, or the next permitted sound is lost in every tab.
        if (deps.soundAccepted?.() ?? true) {
          soundPlayed = true;
          deps.coordinator.commitAlert(soundKey);
          deps.onSound(cfg.soundVolume);
        }
      }
      if (decision.toast) deps.onToast(item);
    }, itemKey);
    if (soundPlayed) {
      rememberBurst(soundKey, at);
    }
    return alerted;
  }

  return {
    drainTo,
    /**
     * Reconnect/ready catch-up: from this tab's own last-alerted revision up to
     * the current summary revision. Bounded to one request, so a tab returning
     * after a long disconnection alerts for at most one page instead of a flood.
     */
    async catchUp(summaryRevision) {
      const before = deps.getOwnCreatedRevision() ?? '0';
      if (!isRevisionAdvance(before, summaryRevision)) return null;
      const target = String(summaryRevision);
      let total: DrainResult = { throughCreatedRevision: target, drained: 0, alerted: 0 };
      for (let pass = 0; pass < RECONNECT_DRAIN_PASSES; pass += 1) {
        const start = deps.getOwnCreatedRevision() ?? '0';
        const result = await drainTo(target);
        if (!result) return null;
        total = {
          throughCreatedRevision: target,
          drained: total.drained + result.drained,
          alerted: total.alerted + result.alerted,
        };
        // Nothing came back: the watermark was already current.
        if (result.drained === 0) break;
        // `drainTo` could not advance past `start` (server ignores `after`).
        if (start === target) break;
      }
      deps.setOwnCreatedRevision(target);
      return total;
    },
  };
}
